/* eslint-disable @typescript-eslint/no-misused-promises */
import CloseIcon from "@mui/icons-material/Close";
import { Box, Grid, IconButton, Typography } from "@mui/material";
import {
  addDays,
  endOfDay,
  isWithinInterval,
  startOfDay,
  startOfWeek,
} from "date-fns";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BerthListDto,
  BerthOrderListDto,
  useBerthApi,
} from "~/api/GeneratedApi";
import { WeekDatePicker } from "~/components/WeekDatePicker";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import { BerthOrdersDayList } from "./BerthOrdersDayList";

export const SHIP_COLORS = [
  "#9FA8DA",
  "#3F51B5",
  "#1A237E",
  "#8C9EFF",
  "#536DFE",
  "#3D5AFE",
  "#2196F3",
  "#64B5F6",
  "#80DEEA",
  "#00ACC1",
  "#006064",
  "#00B8D4",
  "#009688",
  "#80CBC4",
  "#004D40",
  "#64FFDA",
  "#1DE9B6",
  "#827717",
  "#00BFA5",
  "#4CAF50",
  "#A5D6A7",
  "#66BB6A",
  "#388E3C",
  "#FF7043",
  "#2E7D32",
  "#1B5E20",
  "#B9F6CA",
  "#69F0AE",
  "#00E676",
  "#00C853",
  "#8BC34A",
  "#C5E1A5",
  "#AED581",
  "#9CCC65",
  "#8BC34A",
  "#7CB342",
  "#689F38",
  "#558B2F",
  "#33691E",
  "#CCFF90",
  "#B2FF59",
  "#76FF03",
  "#64DD17",
  "#CDDC39",
  "#C0CA33",
  "#AFB42B",
  "#9E9D24",
  "#F4FF81",
  "#EEFF41",
  "#C6FF00",
  "#AEEA00",
  "#FFEB3B",
  "#FFF9C4",
  "#FFF59D",
  "#FFF176",
  "#FFEE58",
  "#FFEB3B",
  "#FDD835",
  "#FBC02D",
  "#F9A825",
  "#F57F17",
  "#FFFF8D",
  "#FFFF00",
  "#FFEA00",
  "#FFD600",
  "#FFC107",
  "#FFECB3",
  "#FFE082",
  "#FFD54F",
  "#FFCA28",
  "#FFC107",
  "#FFB300",
  "#FFA000",
  "#FF8F00",
  "#FF6F00",
  "#FFE57F",
  "#FFD740",
  "#FFC400",
  "#FFAB00",
  "#FF9800",
  "#FFE0B2",
  "#FFCC80",
  "#FFB74D",
  "#FFA726",
  "#FF9800",
  "#FB8C00",
  "#F57C00",
  "#EF6C00",
  "#E65100",
  "#FFD180",
  "#FFAB40",
  "#FF9100",
  "#FF6D00",
  "#FF5722",
  "#FFCCBC",
  "#FFAB91",
  "#FF8A65",
  "#BF360C",
  "#FF9E80",
  "#FF6E40",
  "#FF3D00",
  "#DD2C00",
];

interface Props {
  berth: BerthListDto;
  onClose?: () => void;
}

interface Weekday {
  date: Date;
  orders: BerthOrderListDto[];
}

const BerthOrdersList = ({ berth, onClose }: Props): JSX.Element => {
  const berthApi = useBerthApi();
  const [date, setDate] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 }),
  );
  const [weekdays, setWeekdays] = useState<Weekday[]>();
  const [shipColors, setShipColors] = React.useState<Record<string, string>>(
    {},
  );

  /* const { t } = */ useTranslation("berths");

  // Filter out dates outside the currently selected week
  const { data: berthOrders, refetch } = useCustomQuery(
    { key: "berth-orders", id: berth.id },
    () => berthApi.getBerthOrders(berth.id),
    {
      select: (orders) => orders.results,
    },
  );

  useEffect(() => {
    const days: Weekday[] = [];
    const weekStart = startOfWeek(date, { weekStartsOn: 1 });
    let allOrders: Set<BerthOrderListDto> = new Set();
    for (let i = 0; i < 7; i++) {
      const d = addDays(weekStart, i);
      const orders =
        berthOrders?.filter(
          (o) =>
            isWithinInterval(d, {
              start: o.startTimestamp,
              end: o.endTimestamp,
            }) ||
            isWithinInterval(o.startTimestamp, {
              start: startOfDay(d),
              end: endOfDay(d),
            }) ||
            isWithinInterval(o.endTimestamp, {
              start: startOfDay(d),
              end: endOfDay(d),
            }),
        ) ?? [];
      allOrders = new Set([...allOrders.values(), ...orders]);
      days.push({
        date: d,
        orders,
      });
    }
    setWeekdays(days);

    const uniqueShips = new Set(
      Array.from(allOrders).map((x) => x.vessel!.mmsi),
    );

    setShipColors(
      Array.from(uniqueShips)
        .sort((a, b) => a - b)
        .reduce(
          (tot, curr, index) => ({ ...tot, [curr]: SHIP_COLORS[index] }),
          {},
        ),
    );
  }, [berthOrders, date]);

  const renderedWeekdays = useMemo(
    () =>
      weekdays?.map((w, index) => (
        <BerthOrdersDayList
          date={w.date}
          key={w.date.valueOf()}
          firstColumn={index === 0}
          berthId={berth.id}
          refetch={() => refetch()}
          orders={w.orders}
          colors={shipColors}
        />
      )),
    [berth.id, refetch, shipColors, weekdays],
  );

  return (
    <Box style={{ minWidth: 600 }}>
      <Grid
        container
        alignItems="center"
        sx={{
          position: "sticky",
          left: 0,
          top: 0,
          backgroundColor: (theme) => theme.palette.background.paper,
          zIndex: (theme) => theme.zIndex.modal + 1,
          paddingTop: 1,
          maxWidth: "100vw",
        }}
      >
        <Grid item>
          <Typography variant="h6" noWrap sx={{ paddingLeft: 1 }}>
            {berth.name}
          </Typography>
        </Grid>
        <Grid item style={{ marginLeft: "auto" }}>
          <WeekDatePicker
            date={date}
            onDateChange={(d) => {
              if (d) setDate(d);
            }}
          />
        </Grid>
        {onClose && (
          <Grid item>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Grid>
        )}
      </Grid>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "nowrap",
          maxWidth: "100%",
          overflow: "auto",
          justifyContent: { xs: "unset", md: "center" },
        }}
      >
        {renderedWeekdays}
      </Box>
    </Box>
  );
};

export default BerthOrdersList;
